import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Edit, Trash2, ArrowLeft, Monitor, Wrench } from 'lucide-react';
import { cctvApi } from '@/api/cctv';
import { maintenanceApi } from '@/api/maintenance';
import type { CCTV, Maintenance } from '@/types';
import LoadingSpinner from '@/components/LoadingSpinner';
import ConfirmDialog from '@/components/ConfirmDialog';
import EmptyState from '@/components/EmptyState';
import DataTable from '@/components/DataTable';
import { formatDate, formatStatus, getStatusColor } from '@/utils/formatters';

const statusLabels: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  maintenance: 'Mantenimiento',
};

export function CCTVDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDelete, setShowDelete] = useState(false);

  const { data: cctvRes, isLoading, error } = useQuery({
    queryKey: ['cctv', id],
    queryFn: () => cctvApi.getById(id!),
    enabled: !!id,
  });

  const { data: maintRes } = useQuery({
    queryKey: ['maintenance', 'by-cctv', id],
    queryFn: () => maintenanceApi.getAll({ limit: 50, ...({ cctvId: id } as any) }),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => cctvApi.remove(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cctv'] });
      toast.success('Equipo CCTV eliminado exitosamente');
      navigate('/cctv');
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Error al eliminar equipo CCTV');
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !cctvRes?.data?.data) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-danger-200 bg-danger-50 p-8 text-center">
        <Monitor className="mb-2 h-12 w-12 text-danger-300" />
        <p className="text-sm font-medium text-danger-700">Equipo CCTV no encontrado</p>
        <button
          type="button"
          onClick={() => navigate('/cctv')}
          className="mt-3 rounded-md bg-danger-100 px-3 py-1.5 text-sm font-medium text-danger-700 hover:bg-danger-200 transition-colors"
        >
          Volver a CCTV
        </button>
      </div>
    );
  }

  const cctv = cctvRes.data.data;
  const maintenanceList = (maintRes?.data?.data || []) as (Maintenance & { type?: string })[];

  const statusDisplay = statusLabels[cctv.status] || cctv.status;
  const statusColor = getStatusColor(cctv.status);

  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDelete(false);
  };

  const maintColumns = [
    {
      key: 'scheduledDate',
      label: 'Fecha',
      sortable: true,
      render: (item: any) => formatDate(item.scheduledDate || item.createdAt),
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (item: any) => (
        <span className="inline-flex items-center rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-700">
          {item.type ? formatStatus(item.type) : '-'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      sortable: true,
      render: (item: Maintenance) => {
        const s = item.status.replace(/_/g, '-');
        return (
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(s)}`}>
            {formatStatus(s)}
          </span>
        );
      },
    },
    {
      key: 'assignedUser',
      label: 'Técnico',
      render: (item: any) => item.assignedUser?.name || item.technician || item.assignedTo || '-',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/cctv')}
            className="rounded-md p-2 text-surface-400 hover:bg-surface-100 hover:text-surface-600 transition-colors"
            aria-label="Volver"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-bold text-surface-900">
            {cctv.brand} {cctv.model}
          </h1>
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColor}`}>
            {statusDisplay}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(`/cctv/${id}/edit`)}
            className="inline-flex items-center gap-2 rounded-md border border-surface-300 bg-white px-3 py-2 text-sm font-medium text-surface-700 shadow-sm hover:bg-surface-50 transition-colors"
          >
            <Edit className="h-4 w-4" />
            Editar
          </button>
          <button
            type="button"
            onClick={() => setShowDelete(true)}
            className="inline-flex items-center gap-2 rounded-md border border-danger-300 bg-white px-3 py-2 text-sm font-medium text-danger-700 shadow-sm hover:bg-danger-50 transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Eliminar
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-surface-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-surface-900">Información del Equipo</h2>
          <dl className="space-y-3">
            <div className="flex items-start gap-3">
              <Monitor className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Marca</dt>
                <dd className="text-sm text-surface-900">{cctv.brand || '-'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Monitor className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Modelo</dt>
                <dd className="text-sm text-surface-900">{cctv.model || '-'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Monitor className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Número de Serie</dt>
                <dd className="text-sm text-surface-900">{cctv.serialNumber || '-'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Monitor className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Cliente</dt>
                <dd className="text-sm text-surface-900">{cctv.client?.name || '-'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Monitor className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Ubicación</dt>
                <dd className="text-sm text-surface-900">{cctv.location || '-'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Monitor className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Fecha de Instalación</dt>
                <dd className="text-sm text-surface-900">{formatDate(cctv.createdAt)}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Monitor className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Dirección IP</dt>
                <dd className="text-sm text-surface-900">{cctv.ipAddress || '-'}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Monitor className="mt-0.5 h-4 w-4 text-surface-400" />
              <div>
                <dt className="text-xs font-medium text-surface-500">Puerto</dt>
                <dd className="text-sm text-surface-900">{cctv.port != null ? cctv.port : '-'}</dd>
              </div>
            </div>
            {(cctv as any).installationDate && (
              <div className="flex items-start gap-3">
                <Monitor className="mt-0.5 h-4 w-4 text-surface-400" />
                <div>
                  <dt className="text-xs font-medium text-surface-500">Instalado el</dt>
                  <dd className="text-sm text-surface-900">{formatDate((cctv as any).installationDate)}</dd>
                </div>
              </div>
            )}
            {cctv.notes && (
              <div className="flex items-start gap-3">
                <Monitor className="mt-0.5 h-4 w-4 text-surface-400" />
                <div>
                  <dt className="text-xs font-medium text-surface-500">Notas</dt>
                  <dd className="text-sm text-surface-900 whitespace-pre-wrap">{cctv.notes}</dd>
                </div>
              </div>
            )}
          </dl>
        </div>

        <div className="rounded-lg border border-surface-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-surface-900">
            <Wrench className="h-5 w-5 text-surface-400" />
            Historial de Mantenimientos
          </h2>
          {maintenanceList.length > 0 ? (
            <DataTable
              columns={maintColumns}
              data={maintenanceList}
              onRowClick={(item: any) => navigate(`/maintenance/${item.id}`)}
            />
          ) : (
            <EmptyState
              title="Sin mantenimientos"
              description="Este equipo no tiene mantenimientos registrados"
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="Eliminar Equipo CCTV"
        message={`¿Estás seguro de eliminar ${cctv.brand} ${cctv.model}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}

export default CCTVDetail;
